"use client";

import { useEffect, useMemo, useState } from "react";
import Link from "next/link";
import Image from "next/image";
import { m } from "motion/react";
import {
  Shield,
  Users,
  Award,
  Star,
  Compass,
  Music,
  Tent,
  Utensils,
  CheckCircle2,
  ChevronRight,
  Sparkles,
} from "lucide-react";
import { ICMS } from "@/types/home.types";
import { createLookupObject } from "@/utils/helper";
import FAQ from "./FAQ";
import { useAppSelector } from "@/hooks/hooks";

interface AboutUsClientProps {
  cmsData?: ICMS[];
}

export default function AboutUsClient({ cmsData }: AboutUsClientProps) {
  const [lookUpCMSData, setLookUpCMSData] = useState<Record<string, ICMS>>({});
  const BannerList = useAppSelector((state) => state.home.BannerList);
  // Banners info
  const banner: any = useMemo(() => {
    return (
      (BannerList || []).find((b: any) => b?.BannerType === "about-us") || {}
    );
  }, [BannerList]);

  useEffect(() => {
    if (cmsData && cmsData.length > 0) {
      setLookUpCMSData(createLookupObject(cmsData, "Name"));
    }
  }, [cmsData]);

  // Fade-in animation variants
  const fadeInUp = {
    hidden: { opacity: 0, y: 30 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.6, ease: "easeOut" },
    },
  } as const;

  const staggerContainer = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.2,
      },
    },
  };

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Banner Section */}
      <section className="relative h-[45vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <Image
            src={
              banner?.BannerImage ||
              "https://images.novotrips.com/SOLARMITRA/Uploads/Ranutsav/documents/639172950444853797.png"
            }
            alt={
              banner?.AltTitle || banner?.BannerTitle || "Rann of Kutch Desert"
            }
            fill
            priority
            className="object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/30 via-black/40 to-black/80"></div>
        </div>

        <div className="relative z-10 text-center px-4 max-w-4xl">
          <m.span
            initial={{ opacity: 0, y: -20 }}
            animate={{ opacity: 1, y: 0 }}
            className="inline-block px-4 py-1.5 rounded-full bg-white/10 backdrop-blur-md border border-white/20 text-yellow-300 font-semibold text-sm uppercase tracking-wider mb-4"
          >
            {lookUpCMSData[
              "About Section: Discover the Magic of Rann Utsav Gujarat"
            ]?.MetaTitle || "About Us"}
          </m.span>
          <m.h1
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.1 }}
            className="text-4xl md:text-6xl font-extrabold text-white mb-6 drop-shadow-lg font-sans"
          >
            {banner?.BannerTitle || "About Rann Utsav"}
          </m.h1>
          <m.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.2 }}
            className="text-lg md:text-xl text-gray-200 font-sans font-light drop-shadow-md leading-relaxed"
          >
            {banner?.Description ||
              "Discover the story behind India's premier white desert festival and our dedication to crafting ultra-luxury journeys."}
          </m.p>
        </div>
      </section>

      {/* Welcome / Our Story Section */}
      {lookUpCMSData[
        "About Section: Discover the Magic of Rann Utsav Gujarat"
      ] && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Left Content Column */}
              <m.div
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5 }}
                className="lg:col-span-7 space-y-6"
                dangerouslySetInnerHTML={{
                  __html:
                    lookUpCMSData[
                      "About Section: Discover the Magic of Rann Utsav Gujarat"
                    ]?.Template,
                }}
              >
                {/* <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-pink-50 border border-pink-100 text-pink-700 text-xs font-semibold tracking-wider uppercase mb-2">
                  <Sparkles className="w-3.5 h-3.5 text-pink-600 animate-pulse" />
                  The White Desert Festival
                </div>
                <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight font-sans">
                  Discover the Magic of{" "}
                  <span className="bg-gradient-to-r from-yellow-500 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                    Rann Utsav Gujarat
                  </span>
                </h2>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  Rann Utsav, also known as the Rann of Kutch Festival, is
                  India's premier desert carnival held annually in the
                  breathtaking salt desert of Gujarat. This spectacular cultural
                  extravaganza showcases the rich heritage, vibrant folklore,
                  and exquisite natural beauty of the Kutch region, attracting
                  thousands of visitors from across the globe.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  The Great Rann of Kutch, one of the largest salt deserts in
                  the world, transforms into a magical landscape during the
                  winter months. Spanning over 7,500 square kilometers, this
                  pristine white expanse offers an otherworldly experience,
                  especially during full moon nights when the desert glows
                  mystically under the moonlight.
                </p>
                <p className="text-gray-600 leading-relaxed text-lg font-light">
                  As an authorized government-approved partner, we specialize in
                  organizing luxury glamping stays and dynamic desert tour
                  packages. Our goal is to ensure a seamless, high-end vacation
                  experience that blends authentic cultural heritage with
                  contemporary five-star comfort.
                </p> */}
              </m.div>

              {/* Right Images Column */}
              <m.div
                className="lg:col-span-5 relative flex justify-center"
                initial={{ opacity: 0, scale: 0.95 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative w-full max-w-md aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                  <Image
                    src={
                      lookUpCMSData[
                        "About Section: Discover the Magic of Rann Utsav Gujarat"
                      ]?.CMSImage ||
                      "https://images.novotrips.com/SOLARMITRA/Uploads/Ranutsav/documents/639172959332078116.webp"
                    }
                    alt={
                      lookUpCMSData[
                        "About Section: Discover the Magic of Rann Utsav Gujarat"
                      ]?.CMSImageAlt || "Kutchi Folk Performers"
                    }
                    fill
                    className="object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </m.div>
            </div>
          </div>
        </section>
      )}

      {/* Experience Pillars Section */}
      {lookUpCMSData["About - Section: Experience the Core Pillars"] && (
        <section className="py-20 bg-gray-50/55">
          <div
            className="max-w-7xl mx-auto px-6 lg:px-8"
            dangerouslySetInnerHTML={{
              __html:
                lookUpCMSData["About - Section: Experience the Core Pillars"]
                  ?.Template,
            }}
          >
            {/* <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-900 mb-4 font-sans">
              Experience the{" "}
              <span className="bg-gradient-to-r from-yellow-500 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                Core Pillars
              </span>
            </h2>
            <p className="text-gray-600 text-lg font-light leading-relaxed">
              Every detail of our itineraries is designed to showcase the very
              best aspects of Kutch's white desert festival.
            </p>
          </div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8"
            variants={staggerContainer}
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
          >
            {pillars.map((pillar, index) => {
              const Icon = pillar.icon;
              return (
                <motion.div
                  key={index}
                  variants={fadeInUp}
                  className="bg-white rounded-3xl p-8 border border-gray-100 shadow-sm hover:shadow-xl hover:border-pink-200 transition-all duration-300 group"
                >
                  <div className="w-14 h-14 bg-gradient-to-br from-yellow-400 via-pink-500 to-purple-600 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 transition-transform duration-300 shadow-md">
                    <Icon className="w-7 h-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-sans group-hover:text-pink-600 transition-colors">
                    {pillar.title}
                  </h3>
                  <p className="text-gray-600 text-sm leading-relaxed font-light">
                    {pillar.desc}
                  </p>
                </motion.div>
              );
            })}
          </motion.div> */}
          </div>
        </section>
      )}

      {/* Why Book With Us Section */}
      {lookUpCMSData["About Section: Why Choose Us"] && (
        <section className="py-20 bg-white">
          <div className="max-w-7xl mx-auto px-6 lg:px-8">
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16 items-center">
              {/* Left Graphics Column */}
              <m.div
                className="lg:col-span-5 relative order-2 lg:order-1"
                initial={{ opacity: 0, x: -30 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8 }}
              >
                <div className="relative w-full max-w-md aspect-[4/3] rounded-3xl overflow-hidden shadow-2xl border-4 border-white">
                  <Image
                    src={
                      lookUpCMSData["About Section: Why Choose Us"]?.CMSImage ||
                      "https://images.novotrips.com/SOLARMITRA/Uploads/Ranutsav/documents/639172973418056926.webp"
                    }
                    alt={
                      lookUpCMSData["About Section: Why Choose Us"]
                        ?.CMSImageAlt || "Luxury Glamping Tent"
                    }
                    fill
                    className="object-cover hover:scale-105 transition-transform duration-500"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/30 to-transparent"></div>
                </div>
              </m.div>

              {/* Right Copy Column */}
              <m.div
                className="lg:col-span-7 space-y-6 order-1 lg:order-2"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true }}
                variants={fadeInUp}
                dangerouslySetInnerHTML={{
                  __html:
                    lookUpCMSData["About Section: Why Choose Us"]?.Template,
                }}
              >
                {/* <h2 className="text-3xl md:text-4xl font-bold text-gray-900 leading-tight font-sans">
                Why Choose Us for{" "}
                <span className="bg-gradient-to-r from-yellow-500 via-pink-500 to-purple-600 bg-clip-text text-transparent">
                  Your Rann Booking?
                </span>
              </h2>
              <p className="text-gray-600 leading-relaxed text-lg font-light">
                Booking a glamping package in the salt desert can be
                overwhelming with multiple resorts, permit zones, and transfers.
                As an authorized tour operator, we simplify your travel plan
                completely.
              </p>

              <div className="space-y-4 pt-2">
                {[
                  "Official Government Authorized operator for Rann Utsav booking.",
                  "Lowest Rate Guarantee with zero hidden charges or booking fees.",
                  "Comprehensive tour support, including airport transfers from Bhuj and Ahmedabad.",
                  "Curated itineraries featuring Kalo Dungar (Black Hill) and local sightseeing.",
                  "Dedicated 24/7 client support desk to assist you throughout your journey.",
                ].map((point, idx) => (
                  <div
                    key={idx}
                    className="flex items-start gap-3 text-gray-700"
                  >
                    <CheckCircle2 className="w-5.5 h-5.5 text-pink-600 flex-shrink-0 mt-0.5" />
                    <span className="text-base font-light">{point}</span>
                  </div>
                ))}
              </div> */}
              </m.div>
            </div>
          </div>
        </section>
      )}

      {/* Trust Stats / Metrics Banner */}
      {lookUpCMSData["About - Section: Trust Stats"] && (
        <section
          className="py-16 bg-gradient-to-r from-yellow-400 via-pink-500 to-purple-600 text-white"
          dangerouslySetInnerHTML={{
            __html: lookUpCMSData["About - Section: Trust Stats"]?.Template,
          }}
        >
          {/* <div className="grid grid-cols-2 lg:grid-cols-4 gap-8 md:gap-12 text-center">
            {stats.map((stat, idx) => {
              const StatIcon = stat.icon;
              return (
                <div key={idx} className="flex flex-col items-center space-y-2">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center mb-1">
                    <StatIcon className="w-6 h-6 text-white" />
                  </div>
                  <div className="text-3xl md:text-4xl font-black font-sans">
                    {stat.value}
                  </div>
                  <div className="text-sm font-bold uppercase tracking-wider">
                    {stat.title}
                  </div>
                  <div className="text-xs text-white/85 max-w-xs">
                    {stat.desc}
                  </div>
                </div>
              );
            })}
          </div> */}
          {/* </div> */}
        </section>
      )}

      {/* Interactive FAQs Section */}
      <FAQ />
    </div>
  );
}
